import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Progress,
  Spacer,
  useDisclosure,
} from '@chakra-ui/react'
import { DevTool } from '@hookform/devtools'
import { useFetchInvoices } from 'api/invoicing'
import { useUploadDocument } from 'api/vendor-projects'
import {
  createEstimatePdf,
  estimateDefaultFormValues,
  parseFormValuesToPayload,
  parsePayloadToFormValues,
  useCreateEstimate,
  useEstimate,
  useEstimateStatusOptions,
  useUpdateEstimate,
} from 'api/wo-estimates'
import { ConfirmationBox } from 'components/Confirmation'
import Select from 'components/form/react-select'
import { ReadOnlyInput } from 'components/input-view/input-view'
import { ViewLoader } from 'components/page-level-loader'
import { REQUIRED_FIELD_ERROR_MESSAGE } from 'features/project-details/transactions/transaction.constants'
import jsPDF from 'jspdf'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BiCalendar, BiDetail, BiDownload } from 'react-icons/bi'
import { Account } from 'types/account.types'
import { InvoiceStatusValues } from 'types/invoice.types'
import { isValidAndNonEmpty } from 'utils'
import { dateFormat } from 'utils/date-time-utils'
import { useUserProfile, useUserRolesSelector } from 'utils/redux-common-selectors'
import { UploadFilesModal } from '../../components/choose-file/multi-files-upload-modal'
import { EstimateLaborCost } from './estimate-labor-cost'
import { EstimateMaterialCost } from './estimate-material-cost'
import { EstimateTotalInfo } from './estimate-total-info'
import { ESTIMATE } from './estimate.i18n'
import { ESTIMATE_STATUS } from './wo-estimate-status'

const EstimateReadOnlyInfo: React.FC<{ estimate?: any }> = ({ estimate }) => {
  const { t } = useTranslation()
  const { getValues } = useFormContext<any>()
  const formValues = getValues()

  const isEdit = !!estimate?.id

  return (
    <Grid
      templateColumns="repeat(auto-fill, minmax(150px,1fr))"
      gap={{ base: '1rem 20px', sm: '3.5rem' }}
      borderBottom="1px solid #E2E8F0"
      borderColor="gray.200"
      py="5"
    >
      <GridItem>
        <ReadOnlyInput
          label={t(`${ESTIMATE}.dateCreated`)}
          name={'dateCreated'}
          value={isEdit ? dateFormat(new Date(formValues.createdDate as string)) : dateFormat(new Date())}
          Icon={BiCalendar}
        />
      </GridItem>

      <GridItem>
        <ReadOnlyInput
          label={t(`${ESTIMATE}.dateModified`)}
          name={'dateModified'}
          value={isEdit && formValues.modifiedDate ? dateFormat(new Date(formValues.modifiedDate as string)) : '----'}
          Icon={BiCalendar}
        />
      </GridItem>
      <GridItem>
        <ReadOnlyInput
          label={t(`${ESTIMATE}.createdBy`)}
          name="createdBy"
          value={formValues.createdBy as string}
          Icon={BiDetail}
        />
      </GridItem>

      <GridItem>
        <ReadOnlyInput
          label={t(`${ESTIMATE}.modifiedBy`)}
          name={'modifiedBy'}
          value={(formValues.modifiedBy as string) || '----'}
          Icon={BiDetail}
        />
      </GridItem>
    </Grid>
  )
}

export type EstimateFormProps = {
  onClose: () => void
  selectedEstimateId: number | null
  projectData: any
  currentWorkOrder?: any
  vendor: any
  property: any
  vendorTrades: any
}

export const EstimateForm: React.FC<EstimateFormProps> = ({
  onClose,
  selectedEstimateId,
  projectData,
  currentWorkOrder,
  property,
  vendor,
  vendorTrades,
}) => {
  const { t } = useTranslation()

  const { isVendor } = useUserRolesSelector()
  const { isOpen: isInvoiceAlertOpen, onClose: onInvoiceAlertClose, onOpen: onInvoiceAlertOpen } = useDisclosure()

  const { data: estimate, isLoading: isEstimateLoading } = useEstimate(selectedEstimateId, currentWorkOrder?.id)
  const { invoices } = useFetchInvoices({ projectId: projectData?.id })
  const pendingInvoices = invoices?.filter(i =>
    [InvoiceStatusValues.pendingPayment, InvoiceStatusValues.partialPaid].includes(i.status as InvoiceStatusValues),
  )
  const { isOpen: isOpenDocumentModal, onClose: onDocumentModalClose, onOpen: onDocumentModalOpen } = useDisclosure()
  const projectId = projectData?.id
  const { selectOptions: estimateStatusOptions } = useEstimateStatusOptions(estimate)
  const { mutate: createEstimate, isLoading: isCreateEstimateLoading } = useCreateEstimate()
  const { mutate: updateEstimate, isLoading: isUpdateEstimateLoading } = useUpdateEstimate()
  const { mutate: saveDocument } = useUploadDocument({ hideToast: true, projectId })
  const [selectedItem, setSelectedItem] = useState<any>()
  const isFormLoading = false
  const isFormSubmitLoading = isCreateEstimateLoading || isUpdateEstimateLoading

  const { login = '' } = useUserProfile() as Account

  const defaultValues: any = useMemo(() => {
    return estimateDefaultFormValues(login, property, vendor, currentWorkOrder?.id)
  }, [login, property, vendor])

  const formReturn = useForm<any>({
    defaultValues: {
      ...defaultValues,
    },
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
    getValues,
    trigger,
  } = formReturn

  const isShowStatusField = true

  const generateEstimatePdf = (values, handleSave) => {
    let doc = new jsPDF()
    createEstimatePdf({
      doc,
      estimate: values,
      property,
      projectData,
      workOrder: currentWorkOrder,
      hidePrice: false,
      onSave: handleSave,
      isVendor,
    })
  }
  React.useEffect(() => {
    if (!estimate) return

    const values = parsePayloadToFormValues(estimate)

    reset(values)
  }, [estimate])

  const checkPendingInvoicesAmount = (values, pendingInvoices) => {
    if ([ESTIMATE_STATUS.CANCELLED, ESTIMATE_STATUS.DECLINED].includes(values?.status?.value)) return false

    let invoiceGreaterThanEstimateAmnt = false
    const clientLaborCost = values.laborCost.reduce((a, i) => {
      return a + i.itemClientRate
    }, 0)

    const clientMaterialCost = values.materialCost.reduce((a, i) => a + i.itemAmount, 0)

    const finalSow =
      Number(clientLaborCost) + Number(clientMaterialCost) + (projectData?.sowNewAmount - estimate?.totalClientAmount)

    if (pendingInvoices && pendingInvoices?.length > 0) {
      const totalPendingInvoiceAmount = pendingInvoices?.reduce((total, p) => total + (p?.invoiceAmount || 0), 0)

      // Check if the total pending invoice amount is greater than finalSow
      invoiceGreaterThanEstimateAmnt = totalPendingInvoiceAmount > finalSow
      return invoiceGreaterThanEstimateAmnt
    }
    return invoiceGreaterThanEstimateAmnt
  }

  const onSubmit = useCallback(
    async (values: any) => {
      const queryOptions = {
        onSuccess(res) {
          onClose()
          reset()
        },
      }
      if (estimate?.id) {
        if (checkPendingInvoicesAmount(values, pendingInvoices)) {
          onInvoiceAlertOpen()
          return
        }
        const payload = parseFormValuesToPayload(values, currentWorkOrder?.id)
        updateEstimate(payload, queryOptions)
      } else {
        const payload = parseFormValuesToPayload(values, currentWorkOrder?.id)
        createEstimate(payload, queryOptions)
      }
    },
    [createEstimate, onClose, projectId, estimate, updateEstimate, isEstimateLoading, invoices?.length, projectData],
  )

  const onModalClose = () => {
    reset(defaultValues)
    onClose()
  }

  const formValues = watch()
  const isFlatRate = formValues?.flatRate

  const isLaborCostValid = formValues?.laborCost?.some(
    l =>
      isValidAndNonEmpty(l?.itemDesc) &&
      (isFlatRate !== false || isValidAndNonEmpty(l?.itemCost)) &&
      isValidAndNonEmpty(l?.itemVendorRate) &&
      isValidAndNonEmpty(l?.itemClientRate),
  )

  const isMaterialCostValid = formValues?.materialCost?.some(
    l =>
      isValidAndNonEmpty(l?.itemDesc) &&
      isValidAndNonEmpty(l?.itemCost) &&
      isValidAndNonEmpty(l?.itemClientRate) &&
      isValidAndNonEmpty(l?.itemAmount),
  )

  const allFieldsValidated = formValues?.status?.value && isLaborCostValid && isMaterialCostValid

  const downloadEstimatePdf = () => {
    generateEstimatePdf(estimate, savePdfDocument)
  }

  const savePdfDocument = doc => {
    saveDocument(doc)
  }

  const isCancelled = estimate?.estimateStatus === ESTIMATE_STATUS.CANCELLED
  const isPending = estimate?.estimateStatus === ESTIMATE_STATUS.PENDING
  const isStatusDisabled = !selectedEstimateId || (!!isVendor && !isPending)

  const status = useWatch({
    control,
    name: 'status',
  })
  return (
    <Flex direction="column">
      {isFormLoading && <ViewLoader />}

      {isFormSubmitLoading && (
        <Progress size="xs" isIndeterminate position="absolute" top="60px" left="0" width="100%" aria-label="loading" />
      )}

      <FormProvider {...formReturn}>
        <form onSubmit={handleSubmit(onSubmit)} id="newEstimateForm">
          <Flex flex={1} direction="column" minH="600px">
            <EstimateReadOnlyInfo estimate={estimate} />

            <HStack justifyContent="start" mt="10px">
              <Box w="22%">
                <FormControl>
                  <Checkbox
                    id="flatRate"
                    {...register('flatRate')}
                    isChecked={formValues?.flatRate}
                    mt="2.5px"
                    isDisabled={isCancelled}
                  >
                    <FormLabel htmlFor="flatRate" fontSize="14px" color="gray.700" fontWeight={500} display="inline">
                      Flat Rate
                    </FormLabel>
                  </Checkbox>
                </FormControl>
              </Box>

              <Spacer />

              {!!estimate && (
                <Box>
                  <Button
                    variant="cubicle"
                    color="darkPrimary.300"
                    data-testid="generatePdf"
                    onClick={downloadEstimatePdf}
                    marginRight="-156px"
                    zIndex="9999"
                  >
                    <Box
                      width="20px"
                      height="20px"
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      bg="#F2F3F4" // Add your desired background color here
                      borderRadius="6px"
                      marginRight="5px"
                      border="1px solid #CBD5E0"
                    >
                      <BiDownload color="darkPrimary.300" fontSize="14px" />
                    </Box>
                    Download PDF
                  </Button>
                </Box>
              )}

              {isShowStatusField && (
                <Box w="45%">
                  <FormControl isInvalid={!!errors.status}>
                    <HStack w="100%">
                      <FormLabel
                        fontSize="14px"
                        color="gray.700"
                        fontWeight={500}
                        display="inline"
                        mt="10px"
                        ml="165px"
                      >
                        {t(`${ESTIMATE}.status`)}
                      </FormLabel>
                      <Controller
                        control={control}
                        name="status"
                        rules={{
                          required: REQUIRED_FIELD_ERROR_MESSAGE,
                        }}
                        render={({ field, fieldState }) => (
                          <Box w="40%">
                            <Select
                              {...field}
                              options={estimateStatusOptions}
                              isDisabled={isStatusDisabled}
                              onChange={statusOption => {
                                field.onChange(statusOption)
                              }}
                              selectProps={{ isBorderLeft: true }}
                            />
                            <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
                          </Box>
                        )}
                      />
                    </HStack>
                  </FormControl>
                </Box>
              )}
            </HStack>

            <Box>
              <EstimateLaborCost
                onDocumentModalOpen={(e, index) => {
                  setSelectedItem({ itemType: 'laborCost', index })
                  onDocumentModalOpen()
                }}
                vendor={vendor}
                status={status}
                property={property}
                workOrderId={currentWorkOrder?.id}
                workOrder={currentWorkOrder}
                vendorTrades={vendorTrades}
                estimate={estimate}
              />
            </Box>

            <Box mt="10px">
              <EstimateMaterialCost
                onDocumentModalOpen={(e, index) => {
                  setSelectedItem({ itemType: 'materialCost', index })
                  onDocumentModalOpen()
                }}
                vendor={vendor}
                property={property}
                workOrderId={currentWorkOrder?.id}
                estimate={estimate}
              />
            </Box>

            <Box>
              <EstimateTotalInfo />
            </Box>
          </Flex>
        </form>

        <DevTool control={control} />
        {isOpenDocumentModal && (
          <UploadFilesModal
            isOpen={isOpenDocumentModal}
            documentTypes={[{ value: 'images', label: 'Images' }]}
            onClose={onDocumentModalClose}
            handleUpload={files => {
              if (files?.length > 0) setValue(`${selectedItem?.itemType}.${selectedItem?.index}.documents`, [...files])
              onDocumentModalClose()
              trigger(`${selectedItem?.itemType}`)
            }}
            uploadedFiles={getValues()?.[selectedItem?.itemType]?.[selectedItem?.index]?.documents ?? []}
          />
        )}
      </FormProvider>
      <ConfirmationBox
        title="Open Invoices"
        content="Invoice amount should not be greater than AR. Please review the open invoices and update estimates accordingly."
        isOpen={isInvoiceAlertOpen}
        onClose={onInvoiceAlertClose}
        onConfirm={onInvoiceAlertClose}
        showNoButton={false}
        yesButtonText="OK"
      />
      <Divider mt={3}></Divider>
      <HStack justifyContent={estimate?.document?.s3Url ? 'space-between' : 'flex-end'} mt="16px" spacing="16px">
        <Spacer />
        <HStack>
          <Button onClick={onModalClose} variant={'outline'} colorScheme="darkPrimary">
            {t(`${ESTIMATE}.cancel`)}
          </Button>

          <Button
            type="submit"
            form="newEstimateForm"
            colorScheme="darkPrimary"
            variant="solid"
            disabled={isFormSubmitLoading || !allFieldsValidated}
          >
            {t(`${ESTIMATE}.save`)}
          </Button>
        </HStack>
      </HStack>
    </Flex>
  )
}
